import {
	Cpu,
	Gauge,
	Hammer,
	Milestone,
	Package,
	Send,
	TriangleAlert,
	Upload,
	UserMinus,
	Wrench,
} from "lucide-react";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { ChouicHeroScene } from "@/components/blocks/scenes/ChouicScenes";
import { ExpoCost } from "@/components/blocks/scenes/CostCompare";
import { PipelineScene } from "@/components/blocks/scenes/PipelineScene";
import { CardGrid, toCardItems } from "@/components/blocks/sections/CardGrid";
import { Cta } from "@/components/blocks/sections/Cta";
import { Faq } from "@/components/blocks/sections/Faq";
import { ListCard } from "@/components/blocks/sections/ListCard";
import { NumberedPoints } from "@/components/blocks/sections/NumberedPoints";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { ProofCard } from "@/components/blocks/sections/ProofCard";
import { Section } from "@/components/blocks/sections/Section";
import { SectionHeading } from "@/components/blocks/sections/SectionHeading";
import { StickyStory } from "@/components/blocks/sections/StickyStory";
import { Reveal } from "@/components/motion/motion";
import { useSectionViewTracking } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n/use-i18n";
import { ServiceHeroActions } from "./ServiceHeroActions";

const SECTION_IDS = {
	situations: "situations",
	economics: "cout",
	process: "deroulement",
	decisions: "decisions",
	deliverables: "livrables",
	proof: "experience",
	faq: "questions",
	cta: "contact",
} as const;

const SECTIONS = Object.values(SECTION_IDS).map((id) => ({ id, label: id }));

const SITUATION_ICONS = {
	builds: Hammer,
	release: Upload,
	native: Cpu,
	departure: UserMinus,
	adoption: Package,
	roadmap: Milestone,
} as const;

const DECISION_ICONS = {
	tools: Wrench,
	pace: Gauge,
	native: Cpu,
	delivery: Send,
} as const;

export function ExpoMigrationPage() {
	const { dict, locale } = useI18n();
	useSectionViewTracking(SECTIONS, { pageType: "service", locale });
	const page = dict.expoMigration;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
				visual={<PipelineScene className="mx-auto max-w-[34rem]" />}
			>
				<ServiceHeroActions
					need="expoMigration"
					placement="expo_migration_hero"
					primary={page.hero.primaryCta}
					secondary={page.hero.secondaryCta}
					processId={SECTION_IDS.process}
				/>
			</PageHero>

			<Section id={SECTION_IDS.situations}>
				<SectionHeading
					index="01"
					label={page.situations.label}
					title={page.situations.title}
				/>
				<CardGrid
					className="mt-14"
					items={toCardItems(page.situations.items, {
						icons: SITUATION_ICONS,
					})}
				/>
			</Section>

			<Section id={SECTION_IDS.economics} tone="stone">
				<SectionHeading
					index="02"
					label={page.economics.label}
					title={page.economics.title}
					description={page.economics.description}
				/>
				<div className="mt-14 grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
					<Reveal>
						<ExpoCost />
					</Reveal>
					<NumberedPoints
						title={page.economics.reasonsTitle}
						items={page.economics.reasons}
					/>
				</div>
			</Section>

			<Section id={SECTION_IDS.process}>
				<SectionHeading
					index="03"
					label={page.process.label}
					title={page.process.title}
				/>
				<StickyStory
					className="mt-16 md:mt-24"
					steps={Object.entries(page.process.steps).map(([key, step]) => ({
						key,
						title: step.title,
						details: [
							{ label: page.process.workLabel, text: step.work },
							{ label: page.process.resultLabel, text: step.result },
						],
					}))}
					renderVisual={(index) => <PipelineScene stage={index} />}
				/>
			</Section>

			<Section id={SECTION_IDS.decisions} tone="stone">
				<SectionHeading
					index="04"
					label={page.decisions.label}
					title={page.decisions.title}
					description={page.decisions.description}
				/>
				<CardGrid
					className="mt-14"
					columns={4}
					items={toCardItems(page.decisions.items, { icons: DECISION_ICONS })}
				/>
				<Reveal className="mt-6 grid gap-5 rounded-3xl border border-dashed p-7 md:grid-cols-[auto_1fr] md:gap-8 md:p-10">
					<span
						aria-hidden="true"
						className="flex size-11 items-center justify-center rounded-xl bg-signal/10 text-signal-ink"
					>
						<TriangleAlert className="size-5" />
					</span>
					<div>
						<h3 className="text-xl font-semibold tracking-tight">
							{page.limits.title}
						</h3>
						<p className="mt-3 max-w-3xl text-pretty text-muted-foreground">
							{page.limits.description}
						</p>
					</div>
				</Reveal>
			</Section>

			<Section id={SECTION_IDS.deliverables}>
				<ListCard
					className="mx-auto max-w-4xl"
					label={page.deliverables.label}
					title={page.deliverables.title}
					items={page.deliverables.items}
					note={page.deliverables.note}
				/>
			</Section>

			<Section id={SECTION_IDS.proof} tone="night" decor={<NightDecor />}>
				<ProofCard
					{...page.proof}
					to="/{-$locale}/realisations/chouic"
					visual={<ChouicHeroScene />}
				/>
			</Section>

			<Section id={SECTION_IDS.faq} tone="stone">
				<Faq title={page.faq.title} items={page.faq.items} />
			</Section>

			<Cta
				id={SECTION_IDS.cta}
				{...page.cta}
				need="expoMigration"
				placement="expo_migration_cta"
			/>
		</>
	);
}
